import { MODALS_ENUM } from "@constants/Modals";
import { DeleteNoteRequest } from "@services/deleteNote";
import { PostMinerChatMessage } from "@services/http/miners";
import { MinerChatMessage } from "@services/http/miners/dto";
import { RecordResponse } from "@services/http/responses/BaseResponse";
import useApiRequests from "@services/http/useApiRequest";
import { RootState } from "@store";
import { useGlobalNotifs } from "@utils/hooks/useGlobalNotifs";
import { useToasts } from "@utils/hooks/useToasts";
import { makeNotifs } from "@utils/makeNotifs";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { show } from "redux-modal";
import { useMinerContext } from "../../MinerContext";
import { MyFormMinerSettings } from "../MyForm";

interface UseAdminNotesProps {
  minerId: number;
}

export default function useAdminNotesControl({ minerId }: UseAdminNotesProps) {
  const {
    miner: { chat },
  } = useMinerContext();

  const { error } = useToasts();

  const auth = useSelector((state: RootState) => state.auth);
  const requests = useApiRequests();
  const [messages, setMessages] = useState<MinerChatMessage[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [reset, setReset] = useState(0);
  const [isSubmitting, setSubmitting] = useState(false);
  const dispatch = useDispatch();
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (chat) {
      setMessages(chat);
    }
  }, [chat]);

  useEffect(() => {
    document.addEventListener(`miner-${minerId}-chat`, (e) =>
      onSocketMessage(e)
    );
    return () => {
      document.removeEventListener(`miner-${minerId}-chat`, (e) =>
        onSocketMessage(e)
      );
    };
  }, [messages]);

  const onSocketMessage = (e: Event) => {
    // console.log("From socket", e);
    const event = e as CustomEvent;
    if (event && event.detail) {
      const message = event.detail as MinerChatMessage;
      setMessages([...messages, message]);
    }
  };

  const { ok } = makeNotifs();
  const { onSuccess } = useGlobalNotifs();

  const removeMessage = async (id: number | string) => {
    dispatch(
      show(MODALS_ENUM.CONFIRM_MODAL, {
        onConfirm: async () => {
          const response = await requests.enqueueSilent(
            new DeleteNoteRequest(id)
          );
          if (!response) {
            return;
          }
          setMessages(messages.filter((x) => x.Id !== id));
          onSuccess(ok.success);
        },
      })
    );
  };

  const onSubmit = async (e: React.FormEvent<MyFormMinerSettings>) => {
    setSubmitting(true);
    try {
      e.preventDefault();
      const text = e.currentTarget.elements.admin_note.value;
      if (!text.trim()) return;
      const result = await requests.enqueueSilent<
        RecordResponse<MinerChatMessage>
      >(new PostMinerChatMessage(auth.isAdmin, text, minerId.toString()));
      if (result) {
        if (result.Status === "success") {
          setReset((p) => p + 1);
        } else {
          if (result.Message) {
            error(result.Message);
          }
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onToggleDrawwer = (isOpened: boolean) => {
    setIsOpen(isOpened);
  };

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const isCtrlKeys = e.ctrlKey || e.metaKey;
    if (e.key === "Enter" && !isCtrlKeys) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (isCtrlKeys && e.key === 'Enter') {
      setSubmitting(true);
      e.preventDefault();
      formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }
  };

  return {
    formRef,
    isAdmin: auth.isAdmin,
    messages,
    reset,
    isOpen,
    isSubmitting,
    onToggleDrawwer,
    onInputKeyDown,
    onSubmit,
    removeMessage,
  };
}
